import type { CdtLevelPlan } from "./cdtLevelPlan";
import {
  effectiveAttrs,
  isPlaceholderValue,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "./studioTypes";

export const CDT1_MIN_BAY_SHARE = 0.3;

export const selectiveCdt1 = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  plan: CdtLevelPlan,
  walk: readonly string[],
  minimumShare = CDT1_MIN_BAY_SHARE,
): Map<string, string | null> => {
  const result = new Map<string, string | null>();
  const rank = new Map(walk.map((sku, index) => [sku, index]));
  const root = plan.levels[0]?.levels[0]?.attr;
  if (!root) return result;
  for (const branch of plan.branches) {
    if (isPlaceholderValue(branch.value)) continue;
    const parentAttr = branch.tiers[1]?.[0];
    const childAttr = branch.tiers[2]?.[0];
    if (!parentAttr || !childAttr) continue;
    const parents = new Map<string, StudioItem[]>();
    for (const item of items) {
      const attrs = effectiveAttrs(item, draft);
      if (String(attrs[root] ?? "") !== branch.value) continue;
      const parent = String(attrs[parentAttr] ?? "");
      if (isPlaceholderValue(parent)) continue;
      parents.set(parent, [...(parents.get(parent) ?? []), item]);
    }
    for (const siblings of parents.values()) {
      const children = new Map<string, StudioItem[]>();
      for (const item of [...siblings].sort(
        (a, b) => (rank.get(a.skuId) ?? Infinity) - (rank.get(b.skuId) ?? Infinity),
      )) {
        const child = String(effectiveAttrs(item, draft)[childAttr] ?? "");
        if (isPlaceholderValue(child)) continue;
        children.set(child, [...(children.get(child) ?? []), item]);
      }
      if (children.size < 2) continue;
      const qualifies = new Set<string>();
      for (const [child, members] of children) {
        const rows = new Map<string, number>();
        let frontage = 0;
        for (const item of members) {
          const validPlacements = item.placements.flatMap((placement) => {
            const run = runs.find((r) => r.run === placement.run);
            if (
              !run ||
              run.bayWidthCm <= 0 ||
              placement.hFacings <= 0 ||
              !run.shelves.some((shelf) => shelf.rank === placement.shelf)
            )
              return [];
            return [{ placement, bayWidthCm: run.bayWidthCm }];
          });
          const placedFacings = validPlacements.reduce(
            (total, { placement }) => total + placement.hFacings,
            0,
          );
          if (placedFacings === 0) continue;
          // Share the SKU's cap across placements so another row cannot multiply it.
          const capShare = Math.max(
            0,
            Math.min(1, (item.maxFacings ?? placedFacings) / placedFacings),
          );
          for (const { placement, bayWidthCm } of validPlacements) {
            rows.set(`${placement.run}:${placement.shelf}`, bayWidthCm);
            frontage += item.facingWidthCm * placement.hFacings * capShare;
          }
        }
        const capacity = [...rows.values()].reduce((a, b) => a + b, 0);
        if (child && capacity > 0 && frontage / capacity >= minimumShare) qualifies.add(child);
      }
      if (!qualifies.size) continue;
      for (const [child, members] of children) {
        for (const item of members) result.set(item.skuId, child || null);
      }
    }
  }
  return result;
};
